import { ReactElement, MutableRefObject, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Button } from '@upmc/ims.component-library';

import classnames from 'classnames';
import { getExpiringNonEmployees, getWorkQueueRequests } from '../_api';
import { prettyDateFormat } from '../../../../../utils';
import { determineIfExternal, getPath } from 'routes/_utils';
import { Widget } from '../../Widget/Widget';
import { ListDrawer } from '../../../../../components/UI/ListDrawer/ListDrawer';
import { DataTable } from '../../../../../components/UI/MDC/DataTable/DataTable';
import StyleAuxiliary from '../../../../../hoc/Auxiliary/StyleAuxiliary/StyleAuxiliary';
import { NOTIFICATION_TYPE } from '../_constants';
import { CSS_CLASSES, MAX_DRAWERS } from './_constants';
import {
	Subtitle1,
	Subtitle2,
} from '../../../../../components/UI/MDC/Typography/Typography';
import Card from '../../../../../components/UI/MDC/Card/Card';
import classes from './NotificationTable.module.scss';
const NotificationTable = ({ identity = null, notifications }) => {
	const history = useHistory();

	/**
	 * Memoized drawer created for notifications of {@link NOTIFICATION_TYPE} `WORK_QUEUE`.
	 * @type {MutableRefObject}
	 */
	const refDrawerWorkQueueRequests = useRef(null);
	/**
	 * Memoized drawer created for notifications of {@link NOTIFICATION_TYPE} `NON_EMPLOYEE`.
	 * @type {MutableRefObject}
	 */
	const refDrawerExpiredNonEmployees = useRef(null);

	const [sDrawer, setDrawer] = useState(null);
	const [sIndexSelected, setIndexSelected] = useState(null);
	const [sIsRefreshing, setIsRefreshing] = useState(false);

	/**
	 * Helper method for creating a `DataTable` drawer.
	 *
	 * @param {string} label drawer title.
	 * @param {Object[]} headers `DataTable` header objects.
	 * @param {Object[]} rows `DataTable` row objects.
	 * @param {Function} onRowClickedHandler callback to be executed on row interactions.
	 * @param {Boolean} loading toggle `DataTable` loading animation.
	 * @param {Number|null} count nullable total count of rows, rendered and non-rendered.
	 * @returns {ReactElement} rendered `DataTable` drawer.
	 */
	const createDrawer = (
		label,
		headers,
		rows,
		onRowClickedHandler,
		loading = false,
		count = null
	) => {
		return (
			<>
				<DataTable
					headers={headers}
					label={label}
					onRowClickedHandler={onRowClickedHandler}
					rows={rows}
					loading={loading}
				/>
				{rows?.length > 0 && count ? (
					<StyleAuxiliary classes='mdc-form-field'>
						<Subtitle2>
							Showing <strong>{rows.length}</strong> of{' '}
							<strong>{count}</strong>.
						</Subtitle2>
						<Button 
							variant='outlined' 
							onClick={onRowClickedHandler}
							sx={{m: '5px'}}
						>
							View All
						</Button>
					</StyleAuxiliary>
				) : null}
			</>
		);
	};

	/**
	 * Handler on notification interaction. If notification can preview data, synchronously retrieves data and creates drawer for rendering; otherwise, performs redirect.
	 *
	 * @param {Number} indexSelected index of selected drawer.
	 * @returns void
	 */
	const onClickHandler = indexSelected => {
		setIndexSelected(indexSelected);
		if (indexSelected !== null) {
			const notification = notifications[indexSelected];

			const label = notification?.Title ?? notification;
			let arrHeaders = [];
			let onRowClickedHandler = () => {
				if (notification?.Path) {
					if (determineIfExternal(notification.Path.trim())) {
						window.top.location.href = notification.Path.trim();
					} else {
						history.push(getPath(notification.Path));
					}
				}
			}
			let arrRows = [];

			switch (notification?.Type) {
				case NOTIFICATION_TYPE.NON_EMPLOYEE:
					if (refDrawerExpiredNonEmployees?.current === null) {
						arrHeaders = [
							{ Value: 'Name' },
							{ Value: 'Expiration Date' },
						];

						setDrawer(
							createDrawer(
								label,
								arrHeaders,
								arrRows,
								onRowClickedHandler,
								true
							)
						);

						getExpiringNonEmployees({
							Id: identity?.ID,
							Range: MAX_DRAWERS,
						}).then(response => {
							arrRows = response?.['NonEmployees'].map(report => {
								return {
									Key: report.ID.toString(),
									Cells: {
										Name: {
											Value: report.Name.replace(
												',',
												', '
											),
											Validation: {
												Key: 'true',
												Value: {
													ErrorMessage: null,
												},
											},
										},
										Expiration_Date: {
											Value: prettyDateFormat(
												new Date(report.ExpirationDate)
											),
											Validation: {
												Key: 'true',
												Value: {
													ErrorMessage: null,
												},
											},
										},
									},
								};
							});

							refDrawerExpiredNonEmployees.current = createDrawer(
								label,
								arrHeaders,
								arrRows,
								onRowClickedHandler,
								false,
								response?.['Count']
							);

							setDrawer(refDrawerExpiredNonEmployees.current);
						});
					} else {
						setDrawer(refDrawerExpiredNonEmployees.current);
					}
					break;
				case NOTIFICATION_TYPE.WORK_QUEUE:
					if (refDrawerWorkQueueRequests?.current === null) {
						arrHeaders = [
							{ Value: 'Requestee' },
							{ Value: 'Applications' },
							{ Value: 'Request Date' },
							{ Value: 'Work Queues' },
						];

						setDrawer(
							createDrawer(
								label,
								arrHeaders,
								arrRows,
								onRowClickedHandler,
								true
							)
						);

						getWorkQueueRequests({
							ID: identity?.ID,
							Range: MAX_DRAWERS,
						}).then(response => {
							arrRows = response?.['Requests'].map(request => {
								return {
									Key: request.ID.toString(),
									Cells: {
										Requestee: {
											Value: request.RequesteeName.replace(
												',',
												', '
											),
											Validation: {
												Key: 'true',
												Value: {
													ErrorMessage: null,
												},
											},
										},
										Applications: {
											Value: request.Application.map(
												application => application.Name
											).join(', '),
											Validation: {
												Key: 'true',
												Value: {
													ErrorMessage: null,
												},
											},
										},
										Request_Date: {
											Value: prettyDateFormat(
												new Date(request.RequestDate)
											),
											Validation: {
												Key: 'true',
												Value: {
													ErrorMessage: null,
												},
											},
										},
										Work_Queues: {
											Value: request.Application.map(
												application =>
													application.WorkQueueName
											).join(', '),
											Validation: {
												Key: 'true',
												Value: {
													ErrorMessage: null,
												},
											},
										},
									},
								};
							});

							refDrawerWorkQueueRequests.current = createDrawer(
								label,
								arrHeaders,
								arrRows,
								onRowClickedHandler,
								false,
								response?.['Count']
							);
							setDrawer(refDrawerWorkQueueRequests.current);
						});
					} else {
						setDrawer(refDrawerWorkQueueRequests.current);
					}

					break;
				case NOTIFICATION_TYPE.STAFF_PRIVILEGE:
					onRowClickedHandler();
					break;
				case NOTIFICATION_TYPE.APP_RECONCILIATION:
					onRowClickedHandler();
					break;
				case NOTIFICATION_TYPE.SAA:
					onRowClickedHandler();
					break;
				case NOTIFICATION_TYPE.APP_APPROVALS:
					onRowClickedHandler();
					break;
				default:
					break;
			}
		} else {
			setDrawer(null);
		}
	};

	/**
	 * Handler on widget refresh. Resets memoized notification data for re-retrieval, triggers refresh animation. Re-retrieves notification data if a drawer is open.
	 * @returns void
	 */
	const onRefreshHandler = () => {
		if (!sIsRefreshing) {
			setIsRefreshing(true);
			refDrawerWorkQueueRequests.current = null;
			refDrawerExpiredNonEmployees.current = null;

			if (sIndexSelected !== null) {
				onClickHandler(sIndexSelected);
			}

			setTimeout(() => {
				setIsRefreshing(false);
			}, 1000);
		}
	};

	return (
		<Widget
			className={classes[CSS_CLASSES.WIDGET]}
			onRefresh={notifications !== null ? onRefreshHandler : null}
			title='Notifications'
			subtitle='Items requiring your attention.'
		>
			{notifications !== null ? (
				<StyleAuxiliary
					classes={classnames([
						{
							[classes[CSS_CLASSES.WIDGET_REFRESHING]]:
								sIsRefreshing,
						},
					])}
				>
					{notifications?.length > 0 ? (
						<ListDrawer
							items={notifications}
							drawer={sDrawer}
							onClick={onClickHandler}
							twoLine={notifications?.[0]?.Subtitle !== ''}
						/>
					) : (
						<Card
							xlarge
							className={classes[CSS_CLASSES.WIDGET_EMPTY]}
						>
							<Subtitle1>
								You have no new notifications.
							</Subtitle1>
						</Card>
					)}
				</StyleAuxiliary>
			) : null}
		</Widget>
	);
};

export { NotificationTable };
